import { InsightFacts } from '@backstage-community/plugin-tech-insights-common';

/**
 * Local-dev-only stand-ins for the three `plugins/scorecard` Tech Insights
 * facts (`team-lint-rollup`, `catalog-hygiene`, `team-profile-completeness`).
 * All three are normally computed by backend fact retrievers reading Postgres
 * tables (`portfolio_analytics_*`, `teams`) that don't exist in local dev, so
 * without this the scorecard tabs only ever render their empty state.
 *
 * Deterministic per-entity-name hashing (not random) so a given team looks
 * the same on every reload, but different teams land on different tiers —
 * enough spread to eyeball gold/silver/bronze/not-started/not-assessed
 * without hand-authoring fixtures per team. See MockTechInsightsClient for
 * how this is wired in, gated by `scorecard.mockFacts`.
 */

const PERCENTAGE_BUCKETS: Array<number | undefined> = [
  undefined, // not-assessed
  0, // not-started
  35, // bronze
  85, // silver
  100, // gold
];

function hashToUnit(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 1000) / 1000;
}

function pickPercentage(seed: string): number | undefined {
  const index = Math.min(
    Math.floor(hashToUnit(seed) * PERCENTAGE_BUCKETS.length),
    PERCENTAGE_BUCKETS.length - 1,
  );
  return PERCENTAGE_BUCKETS[index];
}

function buildTeamLintRollupFacts(entityName: string, now: string) {
  const bucket = pickPercentage(`${entityName}:lint`);
  // reposTotal includes not-assessed repos (see the real schema: "including
  // those with no lint data") — assessedTotal is the passing+failing
  // denominator alone, so a not-assessed team still owns 3 repos (all
  // not-assessed) rather than 0.
  const assessedTotal = bucket === undefined ? 0 : 8;
  const reposPassing =
    bucket === undefined ? 0 : Math.round((bucket / 100) * assessedTotal);
  const reposFailing = assessedTotal - reposPassing;
  const reposNotAssessed = bucket === undefined ? 3 : 0;
  const reposTotal = assessedTotal + reposNotAssessed;
  // Derived from the actual repo counts, not the raw bucket value — the real
  // backend computes passingPercentage from reposPassing/reposFailing, so the
  // mock must too, or the badge and the "N of M assessed" subtitle disagree
  // (e.g. bucket 35% -> 3/8 repos -> the true percentage is 37.5%, not 35%).
  const percentage =
    assessedTotal === 0
      ? undefined
      : Math.round((reposPassing / assessedTotal) * 10000) / 100;
  // The full failing list, matching `reposFailing` exactly — the frontend's
  // own "show 5 then Show all N" cap already handles long lists, so the mock
  // must not also fabricate a partial list here: doing so previously made
  // `failingRepoRefsTruncated` true (mimicking the real backend's rare
  // `maxFailingRepoRefs` cap) even when nothing was actually truncated,
  // showing a "this list is truncated" notice for a false reason. Mirrors
  // `buildCatalogHygieneFacts`, which always returns the full list.
  const failingRepoRefs = Array.from(
    { length: reposFailing },
    (_, i) => `component:default/${entityName}-repo-${i + 1}`,
  );

  return {
    ...(percentage === undefined
      ? { assessmentState: 'not-assessed' as const }
      : { passingPercentage: percentage }),
    reposTotal,
    reposPassing,
    reposFailing,
    reposNotAssessed,
    failingRepoRefs,
    failingRepoRefsTruncated: false,
    linterDataLastSuccessfulRunAt: now,
    linterDataNextScheduledRunAt: now,
  };
}

function buildCatalogHygieneFacts(entityName: string) {
  const bucket = pickPercentage(`${entityName}:hygiene`);
  const reposTotal = bucket === undefined ? 0 : 6;
  const reposPassing =
    bucket === undefined ? 0 : Math.round((bucket / 100) * reposTotal);
  const reposFailing = reposTotal - reposPassing;
  // See buildTeamLintRollupFacts: derive from the actual repo counts so the
  // badge and subtitle never disagree.
  const percentage =
    reposTotal === 0
      ? undefined
      : Math.round((reposPassing / reposTotal) * 10000) / 100;
  const missingFieldPool = [
    'description',
    'techdocs-ref',
    'tags',
    'lifecycle',
  ] as const;
  const failingRepos = Array.from({ length: reposFailing }, (_, i) => ({
    ref: `component:default/${entityName}-repo-${i + 1}`,
    missingFields: missingFieldPool.slice(0, (i % missingFieldPool.length) + 1),
  }));

  return {
    ...(percentage === undefined
      ? { assessmentState: 'not-assessed' as const }
      : { passingPercentage: percentage }),
    reposTotal,
    reposPassing,
    reposFailing,
    failingRepos,
    failingReposTruncated: false,
  };
}

function buildTeamProfileCompletenessFacts(entityName: string) {
  const percentage = pickPercentage(`${entityName}:profile`);
  // Matches the real backend's `CHECKED_FIELDS` (see
  // `teamProfileCompleteness.ts`) — the real criterion ids, not fabricated
  // ones, so the local mock approximates production shape.
  const fieldPool = [
    'description',
    'leadOrPm',
    'techLead',
    'members',
    'slackPublic',
    'slackAlertsNotifications',
    'jiraLink',
  ];
  const totalCount = fieldPool.length;
  const filledCount =
    percentage === undefined ? 0 : Math.round((percentage / 100) * totalCount);

  return {
    ...(percentage === undefined
      ? { assessmentState: 'not-assessed' as const }
      : { passingPercentage: percentage }),
    filledCount,
    totalCount,
    missingFields: fieldPool.slice(filledCount),
    checkedFields: fieldPool,
  };
}

export function buildMockInsightFacts(
  factIds: string[],
  entityName: string,
): InsightFacts {
  // A real current timestamp, not the Unix epoch: `EntityScorecardsTab`
  // renders `linterDataLastSuccessfulRunAt`/`linterDataNextScheduledRunAt`
  // via a relative-time formatter, and the epoch renders as a nonsensical
  // "56 years ago".
  const now = new Date().toISOString();
  const result: InsightFacts = {};

  for (const factId of factIds) {
    if (factId === 'team-lint-rollup') {
      result[factId] = {
        timestamp: now,
        version: '1.0.0',
        facts: buildTeamLintRollupFacts(entityName, now),
      };
    } else if (factId === 'catalog-hygiene') {
      result[factId] = {
        timestamp: now,
        version: '1.0.0',
        facts: buildCatalogHygieneFacts(entityName),
      };
    } else if (factId === 'team-profile-completeness') {
      result[factId] = {
        timestamp: now,
        version: '1.0.0',
        facts: buildTeamProfileCompletenessFacts(entityName),
      };
    }
  }

  return result;
}
